import * as React from "react";
import { Link, useFetcher } from "@remix-run/react";
import { Spinner } from "#app/components/spinner.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { cn, invariantResponse } from "#app/utils/misc.ts";
import { UtensilsIcon } from "lucide-react";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import {
  json,
  redirect,
  type MetaFunction,
  ActionFunctionArgs,
  LoaderFunctionArgs,
} from "@remix-run/node";
import {
  collarnaryAuthenticator,
  requireCollarnaryAnonymous,
} from "#app/utils/auth.server.ts";
import { commitSession, getSession } from "#app/utils/session.server.ts";
import { unverifiedSessionKey } from "#app/routes/resources+/verify.tsx";
import { passwordSchema } from "#app/utils/user-validation.ts";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { z } from "zod";
import { FormStrategy } from "remix-auth-form";
import { AuthorizationError } from "remix-auth";
import { prisma } from "#app/utils/db.server.ts";
import { safeRedirect } from "remix-utils/safe-redirect";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { Field } from "#app/components/forms.tsx";

export const loginFormSchema = z.object({
  password: passwordSchema,
  redirectTo: z.string().optional(),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: loginFormSchema,
  });
  if (submission.status !== "success") {
    return json(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }

  if (!submission.value) {
    return json(
      {
        status: "error",
        submission,
        result: submission.reply(),
      } as const,
      { status: 400 }
    );
  }

  let sessionId: string | null = null;
  try {
    sessionId = await collarnaryAuthenticator.authenticate(
      FormStrategy.name,
      request,
      {
        throwOnError: true,
        context: { formData },
      }
    );
  } catch (error) {
    if (error instanceof AuthorizationError) {
      return json(
        {
          status: "error",
          submission: {
            ...submission,
            error: {
              // show authorization error as a form level error message.
              "": [error.message],
            },
          },
          result: submission.reply(),
        } as const,
        { status: 400 }
      );
    }
    throw error;
  }

  const session = await prisma.collarnarySession.findUnique({
    where: { id: sessionId },
    select: { userId: true, expirationDate: true },
  });
  invariantResponse(session, "newly created session not found");

  const cookieSession = await getSession(request.headers.get("cookie"));
  const keyToSet = collarnaryAuthenticator.sessionKey;
  cookieSession.set(keyToSet, sessionId);
  const { redirectTo } = submission.value;
  const responseInit = {
    headers: {
      "Set-Cookie": await commitSession(cookieSession, {
        expires: session.expirationDate,
      }),
    },
  };
  if (!redirectTo) {
    return json(
      { status: "success", submission, result: submission.reply() } as const,
      responseInit
    );
  } else {
    throw redirect(safeRedirect(redirectTo), responseInit);
  }
}

export async function loader({ request }: LoaderFunctionArgs) {
  await requireCollarnaryAnonymous(request);
  const session = await getSession(request.headers.get("cookie"));
  const error = session.get(collarnaryAuthenticator.sessionErrorKey);
  let errorMessage: string | null = null;
  if (typeof error?.message === "string") {
    errorMessage = error.message;
  }
  return json(
    { formError: errorMessage, unverified: session.has(unverifiedSessionKey) },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
      },
    }
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "Login to JPG Collarnary Cookoff" }];
};
export default function LoginRoute() {
  return (
    <div className="container relative grid h-[100vh] min-h-full flex-col items-center justify-center lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
        <div className="absolute inset-0 bg-zinc-900" />
        <div className="relative z-20 flex items-center text-4xl font-medium">
          <UtensilsIcon className="mr-2 h-12 w-12" />
          JPG Collarnary Cookoff
        </div>
        <div className="relative z-20 mt-auto">
          <blockquote className="space-y-2">
            <img
              src="/img/collarnary-logo.png"
              alt="collarnary logo"
              className="h-20"
            />
            <footer className="text-right text-sm">Have a good time!</footer>
          </blockquote>
        </div>
      </div>
      <div className="lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <div className="relative z-20 flex items-center justify-center text-3xl font-medium lg:hidden">
              <UtensilsIcon className="mr-2 h-10 w-10" />
              JPG Collarnary Cookoff
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Log in</h1>
            <p className="text-sm text-muted-foreground">
              Enter your password below to sign in
            </p>
          </div>
          <UserAuthForm />
          <p className="px-8 text-center text-sm text-muted-foreground">
            By clicking continue, you agree to our{" "}
            <Link
              to="/f/cookoff/tos"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/f/cookoff/privacy"
              className="underline underline-offset-4 hover:text-primary"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  redirectTo?: string;
  formError?: string | null;
}

export function UserAuthForm({
  redirectTo,
  formError,
  className,
  ...props
}: UserAuthFormProps) {
  const loginFetcher = useFetcher<typeof action>();

  const [form, fields] = useForm({
    id: "login",
    defaultValue: { redirectTo },
    constraint: getZodConstraint(loginFormSchema),
    lastResult: loginFetcher.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: loginFormSchema });
    },
    shouldRevalidate: "onBlur",
  });

  const isLoading = loginFetcher.state === "submitting";

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <loginFetcher.Form method="POST" name="login" {...getFormProps(form)}>
        <div className="grid gap-2">
          <div className="grid gap-1">
            {/* <Label className="sr-only" htmlFor="email">
							Email
						</Label> */}
            <Field
              labelProps={{ children: "Password" }}
              inputProps={{
                ...getInputProps(fields.password, { type: "password" }),
                autoFocus: true,
                type: "password",
              }}
              errors={[
                ...(fields.password.errors || []),
                ...(form.errors || []),
              ]}
            />
            {/* <Input
							id="email"
							placeholder="name@example.com"
							type="email"
							autoCapitalize="none"
							autoComplete="email"
							autoCorrect="off"
							disabled={isLoading}
						/> */}
          </div>
          <Button disabled={isLoading}>
            {isLoading && <Spinner showSpinner />}
            Sign In with Email
          </Button>
        </div>
      </loginFetcher.Form>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
